import {
  HttpError,
  InvalidUserNameAndPasswordError,
  MagicLinkTokenExpiredError,
  VerificationCodeExpiredError,
  VerificationCodeSentTooFrequentlyError,
} from '~/errors/errors';
import { CompanyIdAndName } from '~/types/CompanyInfo';
import { LoginType } from '~/types/LoginType';
import { Gateway } from '../gateway';
import { authenticatingNetworkManager } from '../network/authenticatingNetworkManager';
import { MagicRequest, RequestMethod } from '../network/magicRequest';

const AUTH_URL = '/api/auth';

export interface LoginRequest {
  email?: string;
  password?: string;
  domain?: string;
  loginType: LoginType;
  loginToken?: string;
  phone?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  companyId?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  companies: CompanyIdAndName[];
  companyId?: string;
}

export interface MagicLinkRequest {
  email: string;
  companyId?: string;
}

export interface PhoneVerificationCodeRequest {
  phone: string;
  companyId?: string;
}

export interface OtpVerificationCodeRequest {
  phone: string;
  code: string;
}

class AuthGateway extends Gateway {
  public authenticate = async (loginInfo: LoginCredentials): Promise<AuthResponse> => {
    const request: MagicRequest = {
      url: AUTH_URL + '/password',
      method: RequestMethod.POST,
      headers: this.createCommonHeaders(),
      body: JSON.stringify({
        email: loginInfo.email,
        password: loginInfo.password,
        companyId: loginInfo.companyId,
      }),
    };
    try {
      const response: Response = await this.sendRequest(request);
      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && (error.getStatusCode() === 403 || error.getStatusCode() === 400)) {
        throw new InvalidUserNameAndPasswordError();
      }
      throw error;
    }
  };

  public sendMagicLink = async (magicLinkInfo: MagicLinkRequest): Promise<void> => {
    const request: MagicRequest = {
      url: AUTH_URL + '/magic-link',
      method: RequestMethod.POST,
      headers: this.createCommonHeaders(),
      body: JSON.stringify(magicLinkInfo),
    };
    try {
      await this.sendRequest(request);
    } catch (error) {
      if (error instanceof HttpError && error.getStatusCode() === 403 && error.getErrorType() === 'VerificationTooFrequentlyException') {
        throw new VerificationCodeSentTooFrequentlyError();
      }
      throw error;
    }
  };

  public verifyMagicLink = async (token?: string): Promise<AuthResponse> => {
    const request: MagicRequest = {
      url: AUTH_URL + '/magic-link/verify',
      method: RequestMethod.POST,
      headers: this.createCommonHeaders(),
      body: JSON.stringify({
        token,
      }),
    };
    try {
      const response: Response = await this.sendRequest(request);
      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && error.getStatusCode() === 403) {
        throw new MagicLinkTokenExpiredError();
      }
      throw error;
    }
  };

  public sendOtp = async (phoneInfo: PhoneVerificationCodeRequest): Promise<void> => {
    const request: MagicRequest = {
      url: AUTH_URL + '/otp',
      method: RequestMethod.POST,
      headers: this.createCommonHeaders(),
      body: JSON.stringify({
        phone: phoneInfo.phone,
        companyId: phoneInfo.companyId,
      }),
    };
    try {
      await this.sendRequest(request);
    } catch (error) {
      if (error instanceof HttpError && error.getStatusCode() === 403 && error.getErrorType() === 'VerificationTooFrequentlyException') {
        throw new VerificationCodeSentTooFrequentlyError();
      }
      throw error;
    }
  };

  public verifyOtp = async (phoneInfo: OtpVerificationCodeRequest): Promise<AuthResponse> => {
    const request: MagicRequest = {
      url: AUTH_URL + '/otp/verify',
      method: RequestMethod.POST,
      headers: this.createCommonHeaders(),
      body: JSON.stringify({
        phone: phoneInfo.phone,
        otp: phoneInfo.code,
      }),
    };
    try {
      const response: Response = await this.sendRequest(request);
      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && error.getStatusCode() === 403) {
        throw new VerificationCodeExpiredError();
      }
      throw error;
    }
  };
}

export const authGateway = new AuthGateway(authenticatingNetworkManager);
